// src/pages/Reports/ManifestReport.js
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faFileAlt, faTruck, faSpinner, faTimesCircle,
  faChevronDown, faChevronUp, faBox, faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import html2pdf from 'html2pdf.js';

import BASE_URL from '../../api';

const API_URL = `${BASE_URL}/api/report/reportmanifest`;
const CUSTOMER_API_URL = `${BASE_URL}/api/master/customer`;

const ManifestReport = () => {
  const [form, setForm] = useState({ startdate: '', enddate: '', customerid: '' });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCust, setLoadingCust] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ outgoingManifests: [], ingoingManifests: [] });
  const [expanded, setExpanded] = useState({});
  const printRef = useRef();

  const token = () => localStorage.getItem('token');

  /* -------------------------------------------------
     FETCH CUSTOMERS
  ------------------------------------------------- */
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await fetch(CUSTOMER_API_URL, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        const json = await res.json();
        if (json.success) setCustomers(json.data || []);
      } catch (e) { console.error(e); }
      setLoadingCust(false);
    };
    fetchCustomers();
  }, []);

  /* -------------------------------------------------
     FETCH REPORT
  ------------------------------------------------- */
  const fetchReport = async () => {
    const { startdate, enddate, customerid } = form;
    if (!startdate || !enddate || !customerid) {
      setError('All fields are required');
      return;
    }

    setLoading(true);
    setError(null);
    setData({ outgoingManifests: [], ingoingManifests: [] });
    setExpanded({});

    try {
      let json = null;
      let resOk = false;
      try {
        const res = await fetch(API_URL, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ startdate, enddate, customerid }),
        });
        resOk = res.ok;
        json = await res.json();
      } catch (fetchErr) {
        console.warn("Backend reportmanifest network error, falling back:", fetchErr);
      }

      if (resOk && json && json.success) {
        setData({
          outgoingManifests: json.data.outgoingManifests || [],
          ingoingManifests: json.data.ingoingManifests || [],
        });
        return;
      }

      // Fallback: Fetch manifests and online bookings directly to construct report
      console.warn("Backend reportmanifest failed, falling back to direct manifest fetch:", json?.error || json?.message);

      const [resOg, resIg, resBookings] = await Promise.all([
        fetch(`${BASE_URL}/api/outgoingmanifests`, { headers: { Authorization: `Bearer ${token()}` } }).catch(() => null),
        fetch(`${BASE_URL}/api/outgoingmanifests/getincomingmenifest`, { headers: { Authorization: `Bearer ${token()}` } }).catch(() => null),
        fetch(`${BASE_URL}/api/onlinebookings`, { headers: { Authorization: `Bearer ${token()}` } }).catch(() => null)
      ]);

      const jsonOg = resOg ? await resOg.json().catch(() => null) : null;
      const jsonIg = resIg ? await resIg.json().catch(() => null) : null;
      const jsonBookings = resBookings ? await resBookings.json().catch(() => null) : null;

      const rawOg = Array.isArray(jsonOg) ? jsonOg : (jsonOg?.data || []);
      const rawIg = Array.isArray(jsonIg) ? jsonIg : (jsonIg?.data || []);
      const rawBookings = Array.isArray(jsonBookings) ? jsonBookings : (jsonBookings?.data || []);

      const startDateObj = new Date(startdate);
      const endDateObj = new Date(enddate);
      endDateObj.setHours(23, 59, 59, 999);

      // Bookings map for fast lookup and customer check
      const bookingsById = {};
      const matchingBookingIds = new Set();

      rawBookings.forEach(b => {
        if (!b || !b._id) return;
        const bId = b._id.toString();
        bookingsById[bId] = b;

        const bDate = new Date(b.bookingdate || b.createdAt);
        const inDateRange = (!isNaN(bDate.getTime())) ? (bDate >= startDateObj && bDate <= endDateObj) : true;

        const cId = (b.consigneeid?._id || b.consigneeid)?.toString();
        const clientCustId = (b.pickrequestid?.clientid?._id || b.pickrequestid?.clientid)?.toString();
        const consignorCustId = (b.consignorid?._id || b.consignorid)?.toString();

        const matchesCust = (cId === customerid || clientCustId === customerid || consignorCustId === customerid);

        if (inDateRange && matchesCust) {
          matchingBookingIds.add(bId);
        }
      });

      // Filter outgoing manifests
      const filteredOg = rawOg.filter(m => {
        const ogDate = new Date(m.ogdate || m.createdAt);
        const inDateRange = (!isNaN(ogDate.getTime())) ? (ogDate >= startDateObj && ogDate <= endDateObj) : true;

        const online = m.online || [];
        const hasMatchingBooking = online.some(item => {
          const id = (item?._id || item)?.toString();
          return matchingBookingIds.has(id);
        });

        return (hasMatchingBooking || (matchingBookingIds.size === 0 && inDateRange));
      }).map(m => {
        const expandedOnline = (m.online || []).map(item => {
          if (item && typeof item === 'object' && item.docketNumber) return item;
          const id = (item?._id || item)?.toString();
          return bookingsById[id] || item;
        });
        return { ...m, online: expandedOnline };
      });

      const ogIds = new Set(filteredOg.map(m => m._id?.toString()));

      // Filter ingoing manifests
      const filteredIg = rawIg.filter(m => {
        const ogId = (m.outgoingManifestId?._id || m.outgoingManifestId)?.toString();
        const online = m.onlineBooking || [];
        const hasMatchingBooking = online.some(item => {
          const id = (item?._id || item)?.toString();
          return matchingBookingIds.has(id);
        });
        return ogIds.has(ogId) || hasMatchingBooking;
      }).map(m => {
        const expandedOnline = (m.onlineBooking || []).map(item => {
          if (item && typeof item === 'object' && item.docketNumber) return item;
          const id = (item?._id || item)?.toString();
          return bookingsById[id] || item;
        });
        return { ...m, onlineBooking: expandedOnline };
      });

      setData({
        outgoingManifests: filteredOg,
        ingoingManifests: filteredIg,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------
     UI HELPERS
  ------------------------------------------------- */
  const toggleExpand = (type, id) => {
    setExpanded(prev => ({
      ...prev,
      [`${type}-${id}`]: !prev[`${type}-${id}`],
    }));
  };

  const formatDate = d => new Date(d).toLocaleDateString('en-IN');
  const formatTime = d =>
    new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  const getRouteName = manifest => {
    const route = manifest?.routeid;
    const bookings = manifest?.online || manifest?.onlineBooking || [];
    if (route?.name) return route.name;
    const b = bookings[0];
    if (!b) return 'N/A';
    const origin = b.locationid?.areaname;
    const dest = b.topincode?.locationname;
    return origin && dest ? `${origin} to ${dest}` : origin || dest || 'N/A';
  };

  /* -------------------------------------------------
     PDF GENERATION – ALL OUTGOING ON ONE PAGE, ALL INGOING ON ONE PAGE
  ------------------------------------------------- */
  const generatePDF = () => {
    const element = printRef.current;
    const customer = customers.find(c => c._id === form.customerid);
    const customerName = customer?.companyName || customer?.name || 'Unknown';

    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: `Manifest_Report_${form.startdate}_to_${form.enddate}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    html2pdf()
      .set(opt)
      .from(element)
      .toPdf()
      .get('pdf')
      .then(pdf => {
        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(10);
          pdf.text(`Manifest Report | ${customerName} | ${formatDate(form.startdate)} - ${formatDate(form.enddate)}`, 0.6, 0.4);
          pdf.text(`Page ${i} of ${totalPages}`, 7.5, 0.4);
        }
      })
      .save();
  };

  const hasData = data.outgoingManifests.length + data.ingoingManifests.length > 0;

  // Compute customerName for PDF content (inside render)
  const customer = customers.find(c => c._id === form.customerid);
  const customerName = customer?.companyName || customer?.name || 'Unknown';

  /* -------------------------------------------------
     RENDER
  ------------------------------------------------- */
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
      <div className="max-w-6xl mx-auto">

        {/* HEADER + PDF BUTTON */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FontAwesomeIcon icon={faFileAlt} className="text-blue-600" />
            Manifest Report
          </h1>

          {hasData && (
            <button
              onClick={generatePDF}
              className="inline-flex items-center gap-2 px-5 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 shadow-lg transition"
            >
              <FontAwesomeIcon icon={faFilePdf} />
              Download PDF
            </button>
          )}
        </div>

        {/* ERROR */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-2">
            <FontAwesomeIcon icon={faTimesCircle} />
            {error}
          </div>
        )}

        {/* FILTER FORM */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <input type="date" value={form.startdate} onChange={e => setForm({ ...form, startdate: e.target.value })} className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none" />
            <input type="date" value={form.enddate} onChange={e => setForm({ ...form, enddate: e.target.value })} className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none" />
            <select
              value={form.customerid}
              onChange={e => setForm({ ...form, customerid: e.target.value })}
              disabled={loadingCust}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 bg-white outline-none"
            >
              <option value="">Select Customer</option>
              {customers.map(c => (
                <option key={c._id} value={c._id}>{c.companyName || c.name}</option>
              ))}
            </select>
          </div>

          <button onClick={fetchReport} disabled={loading} className="w-full md:w-auto px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg transition-all">
            {loading ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin" />Generating...</> : <><FontAwesomeIcon icon={faSearch} />Generate Report</>}
          </button>
        </div>

        {/* PDF CONTENT – ONE PAGE PER SECTION */}
        <div style={{ display: 'none' }}>
          <div ref={printRef} style={{ padding: '20px', fontFamily: 'Arial, sans-serif', fontSize: '12px' }}>

            {/* ==== OUTGOING SECTION ==== */}
            {data.outgoingManifests.length > 0 && (
              <div style={{ marginBottom: '40px' }}>
                <h1 style={{ textAlign: 'center', margin: '0 0 10px', fontSize: '18px', fontWeight: 'bold' }}>Outgoing Manifests</h1>
                <p style={{ textAlign: 'center', color: '#555', marginBottom: '20px' }}>
                  {customerName} | {formatDate(form.startdate)} to {formatDate(form.enddate)}
                </p>

                {data.outgoingManifests.map(m => (
                  <div key={m._id} style={{ marginBottom: '25px' }}>
                    <h2 style={{ background: '#2563eb', color: 'white', padding: '8px', borderRadius: '6px', margin: '0 0 8px' }}>
                      {m.manifestno} | {formatDate(m.ogdate)} {m.ogtime}
                    </h2>
                    <p style={{ margin: '0 0 8px' }}><strong>Route:</strong> {getRouteName(m)}</p>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6' }}>
                          <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Docket</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Consignee</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Origin</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Dest</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Boxes</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Act.Wt</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Mode</th>
                        </tr>
                      </thead>
                      <tbody>
                        {m.online.map(b => (
                          <tr key={b._id}>
                            <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.docketNumber}</td>
                            <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.consigneeid?.companyName || b.consigneeid?.name}</td>
                            <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.locationid?.areaname || '-'}</td>
                            <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.topincode?.locationname || '-'}</td>
                            <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px', textAlign: 'center' }}>{b.noofbox}</td>
                            <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.actweight} kg</td>
                            <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.mode}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}

            {/* ==== INGOING SECTION (NEW PAGE) ==== */}
            {data.ingoingManifests.length > 0 && (
              <div style={{ pageBreakBefore: 'always', marginTop: '40px' }}>
                <h1 style={{ textAlign: 'center', margin: '0 0 10px', fontSize: '18px', fontWeight: 'bold' }}>Ingoing Manifests (Received)</h1>
                <p style={{ textAlign: 'center', color: '#555', marginBottom: '20px' }}>
                  {customerName} | {formatDate(form.startdate)} to {formatDate(form.enddate)}
                </p>

                {data.ingoingManifests.map(m => {
                  const og = m.outgoingManifestId;
                  return (
                    <div key={m._id} style={{ marginBottom: '25px' }}>
                      <h2 style={{ background: '#059669', color: 'white', padding: '8px', borderRadius: '6px', margin: '0 0 8px' }}>
                        {m.manifestno} | Received: {formatDate(m.createdAt)}
                      </h2>
                      <p style={{ margin: '0 0 8px' }}><strong>From Hub:</strong> {og?.desthubid?.branchname || 'N/A'}</p>
                      <p style={{ margin: '0 0 8px' }}><strong>Route:</strong> {getRouteName(og)}</p>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f3f4f6' }}>
                            <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Docket</th>
                            <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Consignee</th>
                            <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Origin</th>
                            <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Dest</th>
                            <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Boxes</th>
                            <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Weight</th>
                            <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Mode</th>
                          </tr>
                        </thead>
                        <tbody>
                          {m.onlineBooking.map(b => (
                            <tr key={b._id}>
                              <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.docketNumber}</td>
                              <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.consigneeid?.companyName || b.consigneeid?.name}</td>
                              <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.locationid?.areaname || '-'}</td>
                              <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.topincode?.locationname || '-'}</td>
                              <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px', textAlign: 'center' }}>{b.noofbox}</td>
                              <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.actweight} kg</td>
                              <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.mode}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ON-SCREEN CONTENT */}
        <div className="space-y-12">

          {/* OUTGOING MANIFESTS */}
          {data.outgoingManifests.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faTruck} className="text-blue-600" />
                Outgoing Manifests
              </h2>

              {data.outgoingManifests.map(m => {
                const isExpanded = expanded[`out-${m._id}`];
                return (
                  <div key={m._id} className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
                    <div
                      className="p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white cursor-pointer flex justify-between items-center"
                      onClick={() => toggleExpand('out', m._id)}
                    >
                      <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        <span className="font-bold">{m.manifestno}</span>
                        <span className="text-sm">
                          • {formatDate(m.ogdate)} {m.ogtime}
                        </span>
                      </div>
                      <div className="text-right text-sm">
                        <div>{getRouteName(m)}</div>
                        <div className="text-blue-100">{m.online?.length || 0} Booking(s)</div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="p-4 border-t">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                          <div><strong>Route:</strong> {getRouteName(m)}</div>
                          <div><strong>Mode:</strong> {m.modeid}</div>
                          <div><strong>Transport:</strong> {m.transportid?.vehicleno} ({m.transportid?.ownername})</div>
                          <div><strong>Driver:</strong> {m.drivername}</div>
                          <div><strong>Co-loader:</strong> {m.coloader || 'N/A'}</div>
                          <div>
                            <strong>Status:</strong>{' '}
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                              {m.status}
                            </span>
                          </div>
                        </div>

                        {m.online?.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                              <FontAwesomeIcon icon={faBox} className="text-indigo-600" />
                              Bookings ({m.online.length})
                            </h4>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs text-left border">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-3 py-2">Docket</th>
                                    <th className="px-3 py-2">Consignee</th>
                                    <th className="px-3 py-2">Origin Area</th>
                                    <th className="px-3 py-2">Dest Location</th>
                                    <th className="px-3 py-2">Boxes</th>
                                    <th className="px-3 py-2">Act. Wt</th>
                                    <th className="px-3 py-2">Vol. Wt</th>
                                    <th className="px-3 py-2">Mode</th>
                                    <th className="px-3 py-2">Value</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {m.online.map(b => (
                                    <tr key={b._id} className="border-t hover:bg-gray-50">
                                      <td className="px-3 py-2 font-medium">{b.docketNumber}</td>
                                      <td className="px-3 py-2">{b.consigneeid?.companyName || b.consigneeid?.name}</td>
                                      <td className="px-3 py-2">{b.locationid?.areaname || '-'}</td>
                                      <td className="px-3 py-2">{b.topincode?.locationname || '-'}</td>
                                      <td className="px-3 py-2 text-center">{b.noofbox}</td>
                                      <td className="px-3 py-2">{b.actweight} kg</td>
                                      <td className="px-3 py-2">{b.volweight} kg</td>
                                      <td className="px-3 py-2">{b.mode}</td>
                                      <td className="px-3 py-2">₹{b.invoicevalue?.toLocaleString() || 0}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </section>
          )}

          {/* INGOING MANIFESTS */}
          {data.ingoingManifests.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faTruck} className="text-green-600" />
                Incoming Manifests (Received)
              </h2>

              {data.ingoingManifests.map(m => {
                const isExpanded = expanded[`in-${m._id}`];
                const og = m.outgoingManifestId;
                return (
                  <div key={m._id} className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
                    <div
                      className="p-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white cursor-pointer flex justify-between items-center"
                      onClick={() => toggleExpand('in', m._id)}
                    >
                      <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        <span className="font-bold">{m.manifestno}</span>
                        <span className="text-sm">
                          • Received: {formatDate(m.createdAt)}
                        </span>
                      </div>
                      <div className="text-right text-sm">
                        <div>From: {og?.desthubid?.branchname || 'N/A'}</div>
                        <div className="text-green-100">{m.onlineBooking?.length || 0} Booking(s)</div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="p-4 border-t">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                          <div><strong>Outgoing Manifest:</strong> {og?.manifestno}</div>
                          <div><strong>Date:</strong> {formatDate(og?.ogdate)} {og?.ogtime}</div>
                          <div><strong>Transport:</strong> {og?.transportid?.vehicleno} ({og?.transportid?.ownername})</div>
                          <div>
                            <strong>Status:</strong>{' '}
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                              {m.status}
                            </span>
                          </div>
                        </div>

                        {m.onlineBooking?.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-gray-700 mb-2">Received Bookings</h4>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs text-left border">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-3 py-2">Docket</th>
                                    <th className="px-3 py-2">Consignee</th>
                                    <th className="px-3 py-2">Origin Area</th>
                                    <th className="px-3 py-2">Dest Location</th>
                                    <th className="px-3 py-2">Boxes</th>
                                    <th className="px-3 py-2">Weight</th>
                                    <th className="px-3 py-2">Mode</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {m.onlineBooking.map(b => (
                                    <tr key={b._id} className="border-t hover:bg-gray-50">
                                      <td className="px-3 py-2 font-medium">{b.docketNumber}</td>
                                      <td className="px-3 py-2">{b.consigneeid?.companyName || b.consigneeid?.name}</td>
                                      <td className="px-3 py-2">{b.locationid?.areaname || '-'}</td>
                                      <td className="px-3 py-2">{b.topincode?.locationname || '-'}</td>
                                      <td className="px-3 py-2 text-center">{b.noofbox}</td>
                                      <td className="px-3 py-2">{b.actweight} kg</td>
                                      <td className="px-3 py-2">{b.mode}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </section>
          )}

          {/* NO DATA */}
          {!loading && !hasData && (
            <div className="text-center py-12 text-gray-500">
              <p>No manifest data found for the selected date range and customer.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManifestReport;